import { Box3, Group, Mesh, Scene, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import { ThreeDModelInfo } from '../../../../types';

import { MeshoptDecoder } from '../../../../utils/3d/meshopt_decoder.module';

class GLTFAgent {
  gltfLoader: GLTFLoader;

  gltfModelData: Array<{
    gltfModel: Group;
    position: { x: number; y: number; z: number };
    rotation: { heading: number; pitch: number; roll: number };
  }>;

  originOffset: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };

  scene: Scene;

  constructor(
    scene: Scene,
    config?: { originOffset?: { x: number; y: number; z: number } },
  ) {
    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
    this.gltfModelData = [];
    this.scene = scene;
    this.originOffset = config?.originOffset ?? { x: 0, y: 0, z: 0 };
  }

  setOriginOffset(x: number, y: number, z: number) {
    this.originOffset = { x, y, z };
  }

  loadGLTF(
    modelInfo: ThreeDModelInfo,
    options?: { onProgress?: (percent: number) => void },
  ): Promise<{
    group: THREE.Group;
    centerPosition: {
      x: number;
      y: number;
      z: number;
    };
  }> {
    return new Promise((resolve) => {
      this.gltfLoader.load(
        modelInfo.filePath,
        (gltf) => {
          const gltfModel = gltf.scene;

          gltfModel.rotation.set(
            (((modelInfo.rotation?.roll ?? 0) +
              (modelInfo.rotationTuneParams?.roll ?? 0) -
              180) *
              Math.PI) /
              180,
            (((modelInfo.rotation?.heading ?? 0) +
              (modelInfo.rotationTuneParams?.heading ?? 0) -
              180) *
              Math.PI) /
              180,
            (((modelInfo.rotation?.pitch ?? 0) +
              (modelInfo.rotationTuneParams?.pitch ?? 0) -
              180) *
              Math.PI) /
              180,
          );
          gltfModel.scale.set(
            modelInfo.scale,
            modelInfo.scale,
            modelInfo.scale,
          );
          gltfModel.updateMatrixWorld();
          const boundingbox = new Box3().setFromObject(gltfModel);
          const centerVector = boundingbox.getCenter(new Vector3());

          const posX =
            modelInfo.position.x - this.originOffset.x - centerVector.x;
          const posY = modelInfo.position.y - this.originOffset.y;
          const posZ =
            modelInfo.position.z - this.originOffset.z - centerVector.z;

          gltfModel.position.set(posX, posY, posZ);

          this.gltfModelData.push({
            gltfModel,
            position: {
              x: posX,
              y: posY,
              z: posZ,
            },
            rotation: modelInfo.rotation,
          });

          gltfModel.traverse((child) => {
            if (child instanceof Mesh) {
              const mat = child.material as THREE.Material;
              mat.transparent = true;
              mat.opacity = 0.6;
            }
          });

          const centerPosition: {
            x: number;
            y: number;
            z: number;
          } = {
            x: modelInfo.position.x,
            y: modelInfo.position.y + centerVector.y,
            z: modelInfo.position.z,
          };
          resolve({ group: gltf.scene, centerPosition });

          this.scene.add(gltf.scene);
        },
        (event) => {
          const percent = (event.loaded / event.total) * 100;
          options?.onProgress?.(percent);
        },
        () => {
          throw new Error('Failed load GLTF model.');
        },
      );
    });
  }

  dispose() {
    if (this.gltfModelData.length > 0) {
      this.gltfModelData.forEach((gltfModel) => {
        this.scene.remove(gltfModel.gltfModel);
      });
    }
  }
}

export default GLTFAgent;
